import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { NavController, Platform } from '@ionic/angular/lazy';
import { AuthService } from './shared/services/auth.service';
import { AlertService } from './shared/services/alert.service';
import { CustomerService } from './shared/services/customer.service';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from './shared/services/language.service';
import { SplashScreenService } from './shared/services/splash-screen.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.web-platform]': '!isNative',
  },
})
export class AppComponent implements OnInit, OnDestroy {
  readonly isNative = Capacitor.isNativePlatform();
  private splashHidden = false;
  private appUrlOpenHandle: { remove: () => Promise<void> } | null = null;

  constructor(
    private languageService: LanguageService,
    private translateService: TranslateService,
    private authService: AuthService,
    private customerService: CustomerService,
    private splashScreenService: SplashScreenService,
    private platform: Platform,
    private navCtrl: NavController,
    private alertService: AlertService
  ) {
    this.translateService.setFallbackLang('en');
  }

  async ngOnInit(): Promise<void> {
    await this.getLanguage();
    await this.navigateToInitialRoute();
    if (this.isNative) {
      this.registerAppUrlOpenListener();
    }
  }

  private async navigateToInitialRoute(): Promise<void> {
    await this.splashScreenService.checkFirstLaunch();

    if (this.isNative) {
      await this.platform.ready();
    }

    if (this.isNative && this.splashScreenService.isFirstLaunch()) {
      this.navCtrl.navigateRoot(['/splash'], { replaceUrl: true });
      return;
    }

    if (!this.authService.isLoggedIn()) {
      this.navCtrl.navigateRoot(['/login'], { replaceUrl: true });
      return;
    }

    if (!this.isNative && this.hasRequestedWebRoute()) {
      // Let Angular preserve authenticated web deep links and refreshed routes.
      return;
    }

    this.navCtrl.navigateRoot(['/'], { replaceUrl: true });
  }

  async onPageActivated(): Promise<void> {
    if (!this.isNative || this.splashHidden) return;

    // Ionic emits activate after the first page's entrance transition finishes.
    this.splashHidden = true;
    await SplashScreen.hide();
  }

  private hasRequestedWebRoute(): boolean {
    return !['/', '/login', '/splash'].includes(window.location.pathname);
  }

  ngOnDestroy(): void {
    this.appUrlOpenHandle?.remove();
  }

  async getLanguage() {
    await this.languageService.loadLanguage();
  }

  private registerAppUrlOpenListener(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      const redirectUris = [
        environment.msal.nativeRedirectUri,
        environment.msal.androidNativeRedirectUri,
      ];
      if (redirectUris.some((redirectUri) => event.url.startsWith(redirectUri)))
        this.handleAppUrlOpen(event.url);
    }).then((handle) => {
      this.appUrlOpenHandle = handle;
    });
  }

  private handleAppUrlOpen(url: string): void {
    this.authService.handleNativeRedirect(url).subscribe({
      next: async (result) => {
        if (
          result?.account &&
          (await this.authService.rejectNonCustomerRole())
        ) {
          this.navCtrl.navigateRoot(['/login']);
          return;
        }
        if (result?.account) {
          /** Best-effort sync of the signed-in customer's details to their auth provider profile. */
          this.customerService.syncAuthProfile().subscribe({
            error: () => {
              // swallow — non-critical
            },
          });
          this.navCtrl.navigateRoot(['/']);
        } else {
          this.navCtrl.navigateRoot(['/login']);
        }
      },
      error: async () => {
        await this.navCtrl.navigateRoot(['/login']);
        // Social (Google/Apple) sign-in completes through this callback on
        // native; the redirect doesn't say which provider was used.
        this.alertService.errorEvent(
          this.translateService.instant('App.Errors.SignInFailed')
        );
      },
    });
  }
}
